<template>
  <div class="preview-footer">
    <transition name="preview-footer__fade-transition">
      <div class="preview-footer__contents">
        <audio-player 
          v-if="audioDurationSeconds"
          v-show="showControls && !showWaveform"
          :total-time="audioDurationSeconds"
          :current-time="audioCurrentTimeSeconds"
        />
        <audio-trimmer
          v-if="audioBufferWaveformData"
          v-show="showWaveform"
          :current-time="audioCurrentTimeSeconds"
          :total-time="audioDurationSeconds"
          :audio-buffer-waveform-data="audioBufferWaveformData"
          :reset-trim-flag="resetTrimFlag"
        />
        <div class="preview-footer__row" v-show="showControls">
          <audio-controls
            :is-audio-loading="isAudioLoading"
            :track-name="trackName"
            :show-waveform="showWaveform"
            @file-selected="onFileSelected"
            @toggle-waveform="onToggleWaveform()"
            @reset-trim="resetTrimFlag = !resetTrimFlag"
          />
          <orientation-switcher
            v-if="!showWaveform"
            :orientation="orientation"
            @orientation-selected="onOrientationSelected"
          />
          <div
            v-if="!showWaveform"
            class="preview-footer__button-wrapper"
            id="exportButton"
          >
            <big-button
              :text="buttonText"
              :is-disabled="false"
              :is-loading="false"
              type="secondary"
              @click="onExportVideo"
            />
          </div>
          <div class="preview-footer__social-info" v-else>
            <div class="preview-footer__duration-message">
              <information-icon id="infoIconId" @mouseover="toggleHovered = true" @mouseleave="toggleHovered = false" />
              <p>Recommended social media durations</p>
            </div>
            <big-button
              text="Save"
              type="secondary"
              :is-disabled="false"
              :is-loading="false"
              @click="onToggleWaveform"
            />
          </div>
          <tooltip
            :class="['duration-tooltip', { 'duration-tooltip--hovered' : toggleHovered }]"
            reference-element-id="infoIconId"
            placement="top"
            :is-active="toggleHovered">
            <div>
              <p><span>TikTok:</span> 3 sec - 10 min</p>
              <p><span>Instagram Reels:</span> 15 - 60 sec</p>
              <p><span>Instagram Video Post:</span> 3 - 10 min</p>
              <p><span>Spotify Canvas:</span> 3 - 8 sec</p>
            </div>
          </tooltip>
        </div>
      </div>
    </transition>
  </div>
</template>
<script setup lang="ts">
import AudioControls from '@/components/preview/AudioControls.vue'
import OrientationSwitcher from '@/components/preview/OrientationSwitcher.vue'
import BigButton from '@/components/common/BigButton.vue'
import AudioPlayer from '@/components/preview/AudioPlayer.vue'
import { Orientation } from '@/types'
import { computed, ref } from 'vue'
import { useApplicationStore } from '@/store/application-store'
import { useProjectStore } from '@/store/project-store'
import GtmHelper from '@/analytics/gtm-helper'
import useAudioEngine from '@/composables/UseAudioEngine'
import AudioTrimmer from '@/components/preview/AudioTrimmer.vue'
import Tooltip from '@/components/onboarding/Tooltip.vue'
import InformationIcon from '@/assets/InformationIcon.svg'

const showWaveform = ref(false)
const toggleHovered = ref(false)

const props = defineProps<{
  showControls: boolean
  isAudioLoading: boolean
  trackName: string
  orientation: Orientation
}>()

const gtmHelper = GtmHelper.getInstance()
const projectStore = useProjectStore()
const applicationStore = useApplicationStore()

const { audioCurrentTimeSeconds, audioDurationSeconds, audioBufferWaveformData } = useAudioEngine()

const emits = defineEmits<{
  (event: 'orientationSelected', orientation: Orientation): void
  (event: 'fileSelected', file: File): void
  (event: 'openVideoExport'): void
  (event: 'toggleWaveform'): void
}>()

const resetTrimFlag = ref(false)

const buttonText = computed(() => `Export ${props.orientation}`)

const onExportVideo = () => {
  applicationStore.deselectAllEntities()
  emits('openVideoExport')

  gtmHelper.triggerExportGtmEvents(projectStore, audioDurationSeconds.value)
}

const onOrientationSelected = (orientation: Orientation) => {
  emits('orientationSelected', orientation)
}

const onFileSelected = (file: File) => {
  emits('fileSelected', file)
}

const onToggleWaveform = () => {
  showWaveform.value = !showWaveform.value
  emits('toggleWaveform')
}

</script>
<style lang="scss">
.preview-footer {
  background-color: $grey-90;
  padding: 0px 20px;
  min-height: 135px;

  &__contents {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 16px;
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    height: 48px;
    width: 100%;
    align-items: center;
    overflow: hidden;
    button {
      height: 48px;
    }
  }

  &__button-wrapper {
    justify-self: end;
  }
  &__social-info {
    justify-self: end;
    grid-column: 3;
  }
  
  &__duration-message {
    display: inline-flex;
    font-family: $font-regular;
    align-items: center;
    font-size: 14px;
    line-height: 14px;
    gap: 5px;
    color: $white;

    max-width: 185px;
    margin-right: 10px;
    @media (min-width: 1336px) {
      max-width: unset;
      margin-right: 46px;
    }
  }
  &__fade-transition {
    &-enter-active,
    &-leave-active {
      transition: opacity 200ms $animationCurve;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }
}

.duration-tooltip {
  transition: opacity 200ms $animationCurve;
  opacity : 0;
  width: 220px;
  padding: 10px 15px;
  font-size: 12px;
  line-height: 21px;
  font-family: $font-regular;
  z-index: 3;
  &--hovered {
    opacity: 1;
  }
  span {
    font-family: $font-bold;
  }
}
</style>
